import React, { ReactNode, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckIcon, SearchIcon } from "lucide-react";
import { LangChip } from "../ui/LangChip";
import { getLanguage } from "../../utils/language";
import { spring } from "../../utils/motion";
interface LanguageGridProps {
  codes: string[];
  selected?: string;
  onSelect: (code: string) => void;
  disabledReason?: (code: string) => string | undefined;
  meta?: (code: string) => ReactNode;
  searchLabel: string;
  columns?: 2 | 3;
  /** Multi-select mode: every code in this list is shown as selected. */
  selectedCodes?: string[];
}
export function LanguageGrid({
  codes,
  selected,
  onSelect,
  disabledReason,
  meta,
  searchLabel,
  columns = 3,
  selectedCodes
}: LanguageGridProps) {
  const [query, setQuery] = useState("");
  const reduce = useReducedMotion() ?? false;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return codes;
    return codes.filter(c => {
      const l = getLanguage(c);
      return l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q) || c === q;
    });
  }, [codes, query]);
  return <div data-id="element-107" data-mp="k7u8nxdh5rar">
      <label className="flex items-center gap-2.5 rounded-xl border-3 border-line bg-surface px-3.5 transition-[transform,box-shadow] duration-150 ease-out focus-within:-translate-x-0.5 focus-within:-translate-y-0.5 focus-within:shadow-brutal-sm" data-id="element-108" data-mp="k7lxc9x02u1o">
        <SearchIcon className="h-4 w-4 shrink-0 text-ink-2" strokeWidth={2.5} aria-hidden="true" data-id="element-109" data-mp="1cz5ja1kzem7d" />
        <span className="sr-only" data-id="element-110" data-mp="1801b9e9smuim">{searchLabel}</span>
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={`${searchLabel} (${codes.length} languages)`} className="w-full bg-transparent py-3 text-[15px] text-ink placeholder:text-ink-2/70 outline-none" data-id="element-111" data-mp="136getqnum8lj" />
      </label>

      {filtered.length === 0 ? <p className="mt-6 rounded-card border-3 border-dashed border-line/40 px-4 py-8 text-center text-[15px] text-ink-2" data-id="element-112" data-mp="1quzjzxblq59e">
          No languages match “{query}”.
        </p> : <ul className={`mt-4 grid gap-2.5 sm:grid-cols-2 ${columns === 3 ? "lg:grid-cols-3" : ""}`} aria-label="Languages" data-id="element-113" data-mp="3q5u18c5e3clo">
          {filtered.map((code, i) => {
        const l = getLanguage(code);
        const reason = disabledReason?.(code);
        const active = selectedCodes ? selectedCodes.includes(code) : selected === code;
        return <motion.li key={code} initial={reduce ? false : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...spring.smooth,
          delay: reduce ? 0 : Math.min(i, 12) * 0.03
        }} data-id="element-114" data-mp="37b6oytj1mcm4" data-mpi={i}>
                <motion.button type="button" disabled={Boolean(reason)} onClick={() => onSelect(code)} aria-pressed={active} whileHover={reason || reduce ? undefined : {
            y: -2
          }} whileTap={reason ? undefined : {
            scale: 0.97
          }} transition={spring.snappy} className={`focus-ring flex h-full w-full items-center gap-3 rounded-card border-3 border-line px-3.5 py-3 text-left transition-colors duration-150 ${active ? "bg-accent text-night shadow-brutal-sm" : reason ? "cursor-not-allowed border-dashed bg-transparent text-ink-2 opacity-70" : "bg-surface text-ink hover:bg-brand-tint"}`} data-id="element-115" data-mp="qp5kf6ycw0id">
                  <LangChip code={code} size="md" tone={active ? "surface" : reason ? "surface" : "brand"} data-id="element-116" data-mp="33wdj4uwoh994" />
                  <span className="min-w-0 flex-1" data-id="element-117" data-mp="1eh71x8nd04rk">
                    <span className="block truncate text-[15px] font-bold" data-id="element-118" data-mp="19hb09g24wpf6">{l.name}</span>
                    <span className={`block truncate text-sm ${active ? "text-night/75" : "text-ink-2"}`} lang={code} data-id="element-119" data-mp="19jnq0fe4w42s">
                      {reason ?? l.native}
                    </span>
                    {meta && !reason && <span className="mt-1.5 flex flex-wrap gap-1" data-id="element-120" data-mp="1kwo0jq37nq5n">{meta(code)}</span>}
                  </span>
                  {active && <CheckIcon className="h-5 w-5 shrink-0" strokeWidth={3} aria-hidden="true" data-id="element-121" data-mp="1pwac7v3e4ycc" />}
                </motion.button>
              </motion.li>;
      })}
        </ul>}
    </div>;
}